import React, { useEffect, useState } from 'react'
import Layout from '~layout'
import { LoadingArea, CursorPagination } from '@unbounded/unbounded-components'
import { DataFetchDirection } from '~api'
import {
  GetPaginatedOrganizationsQuery,
  GetPaginatedOrganizationsQueryVariables,
  useGetPaginatedOrganizationsLazyQuery,
} from '~api/queries/organizations.generated'
import { OrganizationsListItemFragment } from '~api/fragments/OrganizationsListItem.generated'
import {
  ListingSearchInput,
  StyledListing,
  useListingParams,
  useListingPagination,
  useListingLazyQuery,
  useListingTilesLimit,
} from '~components/Listing'
import OrganizationsList from './OrganizationsList'

const OrganizationsListContainer: React.FC = () => {
  const [dataFetchDirection, setDataFetchDirection] = useState(DataFetchDirection.START_FROM_FIRST)
  const { limit, containerRef } = useListingTilesLimit()
  const { search, setSearch } = useListingParams()
  const searchFilter = search || undefined

  const { isLoading, data, pageInfo, fetchMore, client } = useListingLazyQuery<
    OrganizationsListItemFragment,
    GetPaginatedOrganizationsQuery,
    GetPaginatedOrganizationsQueryVariables
  >({
    dataFetchDirection,
    useLazyQuery: useGetPaginatedOrganizationsLazyQuery,
    variables: {
      limit,
      text: searchFilter,
    },
  })

  const { resetPagination, onPrevClick, onNextClick, onGoToFirst, onGoToLast, canGoToFirst, canGoToLast } = useListingPagination({
    client,
    fetchMore,
    cacheFieldName: 'organizations',
    dataFetchDirection,
    onDirectionChange: setDataFetchDirection,
    pageInfo,
  })

  useEffect(() => {
    resetPagination()
  }, [searchFilter, resetPagination])

  return (
    <Layout>
      <Layout.Content>
        <Layout.Header>
          <Layout.Title>Organizations</Layout.Title>
        </Layout.Header>

        <StyledListing.ListingSearchInput>
          <ListingSearchInput defaultValue={search} onChange={setSearch} placeholder="Search for organizations" />
        </StyledListing.ListingSearchInput>

        <LoadingArea isLoading={isLoading} placeholder="Loading organizations...">
          <StyledListing.ListingListContainer ref={containerRef}>
            <OrganizationsList items={data} placeholder="Nothing found" />
          </StyledListing.ListingListContainer>
        </LoadingArea>

        <StyledListing.ListingPagination>
          <CursorPagination
            {...{ onPrevClick, onNextClick, onGoToFirst, onGoToLast, canGoToFirst, canGoToLast }}
            hasPrevPage={!!pageInfo?.hasPreviousPage}
            hasNextPage={!!pageInfo?.hasNextPage}
            isDisabled={isLoading || !data.length}
          />
        </StyledListing.ListingPagination>
      </Layout.Content>
    </Layout>
  )
}

export default OrganizationsListContainer
